import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TreeNode, TreeSelectionMode } from './tree.types';

/** Recopila recursivamente las claves de nodos con `expandedByDefault` */
function collectExpandedDefaults(nodes: TreeNode[], acc: Set<string>): void {
  for (const node of nodes) {
    if (node.expandedByDefault) acc.add(node.key);
    if (node.children?.length) collectExpandedDefaults(node.children, acc);
  }
}

/** Filtra recursivamente: conserva un nodo si su label coincide O si algún hijo coincide */
function filterNodes(nodes: TreeNode[], query: string): TreeNode[] {
  const q = query.toLowerCase();
  return nodes.reduce<TreeNode[]>((acc, node) => {
    const filteredChildren = node.children?.length
      ? filterNodes(node.children, query)
      : [];
    if (node.label.toLowerCase().includes(q) || filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, []);
}

/**
 * Recopila las claves de todos los nodos que son ancestros de al menos un nodo
 * cuyo label coincide con `query`. Estos nodos deben expandirse para que los
 * resultados del filtro sean visibles.
 */
function collectAncestorKeys(nodes: TreeNode[], query: string): Set<string> {
  const result = new Set<string>();
  const q = query.toLowerCase();

  function traverse(node: TreeNode): boolean {
    const selfMatches = node.label.toLowerCase().includes(q);
    let childMatches = false;
    if (node.children?.length) {
      for (const child of node.children) {
        if (traverse(child)) childMatches = true;
      }
    }
    if (childMatches) result.add(node.key);
    return selfMatches || childMatches;
  }

  for (const node of nodes) traverse(node);
  return result;
}

/**
 * Organismo: árbol jerárquico con expansión/colapso y selección de nodos.
 *
 * Inspirado en PrimeReact Tree. Sin HTTP ni lógica de dominio.
 * Estado UI (expandidos, seleccionados, filtro) gestionado con signals.
 *
 * @example
 * ```html
 * <ui-tree
 *   [nodes]="myNodes"
 *   selectionMode="single"
 *   (nodeSelect)="onSelect($event)"
 * />
 * ```
 */
@Component({
  selector: 'ui-tree',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  templateUrl: './tree.component.html',
  styleUrl: './tree.component.scss',
})
export class UiTreeComponent {
  // ── Inputs ──────────────────────────────────────────────────────────────
  /** Nodos raíz del árbol */
  readonly nodes = input<TreeNode[]>([]);
  /** Modo de selección */
  readonly selectionMode = input<TreeSelectionMode>('single');
  /**
   * Claves seleccionadas externamente (modo controlado).
   * Si se pasa un array no vacío, tiene prioridad sobre la selección interna.
   */
  readonly selectedKeys = input<string[]>([]);
  /** Muestra un spinner mientras se cargan los nodos */
  readonly loading = input(false);
  /** Texto cuando no hay nodos que mostrar */
  readonly emptyMessage = input('Sin resultados');
  /** Habilita el campo de búsqueda/filtro local */
  readonly showFilter = input(false);
  /** Placeholder del campo de filtro */
  readonly filterPlaceholder = input('Buscar...');
  /**
   * Master toggle de iconos. `false` oculta todos los iconos
   * independientemente de `iconLevels` o del valor `icon` de cada nodo.
   * Defecto: `true`.
   */
  readonly showIcons = input(true);
  /**
   * Niveles de profundidad donde se muestran iconos (0 = raíz, 1 = hijos, …).
   * - `null` (defecto): muestra icono en **todos** los niveles si el nodo lo tiene.
   * - `[0, 2]`: muestra icono solo en el nivel 0 y el nivel 2.
   * - `[]`: oculta todos los iconos (equivalente a `showIcons=false`).
   */
  readonly iconLevels = input<number[] | null>(null);
  /**
   * Template opcional de acciones por fila.
   * Contexto: `{ $implicit: TreeNode }`.
   * Si no se pasa, la fila se comporta igual que antes.
   */
  readonly nodeActionsTemplate = input<TemplateRef<{ $implicit: TreeNode }> | null>(null);

  // ── Outputs ─────────────────────────────────────────────────────────────
  /** Nodo seleccionado */
  readonly nodeSelect = output<TreeNode>();
  /** Nodo deseleccionado */
  readonly nodeUnselect = output<TreeNode>();
  /** Nodo expandido */
  readonly nodeExpand = output<TreeNode>();
  /** Nodo colapsado */
  readonly nodeCollapse = output<TreeNode>();
  /** Claves seleccionadas actualizadas (array de strings) */
  readonly selectionChange = output<string[]>();

  // ── Estado UI interno ────────────────────────────────────────────────────
  protected readonly expandedKeys = signal<Set<string>>(new Set());
  protected readonly internalSelectedKeys = signal<Set<string>>(new Set());
  protected readonly filterQuery = signal('');

  // ── Computed ─────────────────────────────────────────────────────────────
  /** Nodos visibles tras aplicar el filtro (si está activo) */
  protected readonly visibleNodes = computed<TreeNode[]>(() => {
    const q = this.filterQuery().trim();
    return q ? filterNodes(this.nodes(), q) : this.nodes();
  });

  /** Selección activa: usa las claves externas si se proporcionan */
  protected readonly activeSelection = computed<Set<string>>(() => {
    const ext = this.selectedKeys();
    return ext.length ? new Set(ext) : this.internalSelectedKeys();
  });

  constructor() {
    // Inicializar nodos expandidos por defecto al recibir la primera emisión
    effect(
      () => {
        const defaults = new Set<string>();
        collectExpandedDefaults(this.nodes(), defaults);
        if (defaults.size) {
          this.expandedKeys.update((current) => new Set([...current, ...defaults]));
        }
      },
      { allowSignalWrites: true },
    );
  }

  // ── Helpers para el template ──────────────────────────────────────────────
  protected isExpanded(key: string): boolean {
    return this.expandedKeys().has(key);
  }

  protected isSelected(key: string): boolean {
    return this.activeSelection().has(key);
  }

  protected hasChildren(node: TreeNode): boolean {
    return !node.leaf && !!node.children?.length;
  }

  /**
   * Determina si se debe renderizar el icono de un nodo.
   * Combina `showIcons` (master toggle) e `iconLevels` (filtro por nivel).
   */
  protected shouldShowIcon(node: TreeNode, depth: number): boolean {
    if (!node.icon) return false;
    if (!this.showIcons()) return false;
    const levels = this.iconLevels();
    if (levels !== null) return levels.includes(depth);
    return true;
  }

  // ── Handlers ─────────────────────────────────────────────────────────────

  /**
   * Handler unificado de la fila completa del nodo.
   * Expande/colapsa si tiene hijos y, según el modo, selecciona el nodo.
   */
  protected onNodeRowClick(node: TreeNode): void {
    if (node.disabled) return;
    if (this.hasChildren(node)) {
      this.toggleExpand(node);
    }
    this.selectNode(node);
  }

  protected toggleExpand(node: TreeNode): void {
    if (node.disabled) return;

    const keys = new Set(this.expandedKeys());
    if (keys.has(node.key)) {
      keys.delete(node.key);
      this.nodeCollapse.emit(node);
    } else {
      keys.add(node.key);
      this.nodeExpand.emit(node);
    }
    this.expandedKeys.set(keys);
  }

  protected selectNode(node: TreeNode): void {
    if (node.disabled || this.selectionMode() === 'none') return;

    const mode = this.selectionMode();
    const sel = new Set(this.internalSelectedKeys());
    const wasSelected = sel.has(node.key);

    if (mode === 'single') {
      sel.clear();
      if (!wasSelected) {
        sel.add(node.key);
        this.nodeSelect.emit(node);
      } else {
        this.nodeUnselect.emit(node);
      }
    } else {
      if (wasSelected) {
        sel.delete(node.key);
        this.nodeUnselect.emit(node);
      } else {
        sel.add(node.key);
        this.nodeSelect.emit(node);
      }
    }

    this.internalSelectedKeys.set(sel);
    this.selectionChange.emit([...sel]);
  }

  protected onFilterInput(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.filterQuery.set(query);

    const q = query.trim();
    if (q) {
      const ancestors = collectAncestorKeys(this.nodes(), q);
      this.expandedKeys.update((current) => new Set([...current, ...ancestors]));
    }
  }
}
